import { inject, Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import { EntityApiService } from "../../../../shared/store/entity-store/entity-api.service";
import { CommandeClient } from "../../models/commande-client.model";
import { environment } from "../../../../../environments/environment";
import { CommandeClientLigne } from "../../models/commande-client-ligne";
import { CommandeClientEtat } from "../../enums/etat-commande-client.enum";
import { CommandeClientMailPayload } from "../../models/commande-client-mail-payload.model";
import { safeFirstValueFrom } from "../../../../shared/utils/safe-first-value-from";
import { Palette } from "../../../palette/models/palette.model";
import { Adresse } from "../../../adresse/models/adresse.model";
import { VracExpeditionItem } from "../../../expedition-commande/models/vrac-expedition.model";

@Injectable({ providedIn: "root" })
export class CommandeClientApiService
  implements EntityApiService<CommandeClient>
{
  httpClient = inject(HttpClient);

  loadOne(id: string): Observable<CommandeClient> {
    return this.httpClient.get<CommandeClient>(
      `${environment.api()}/commandes_client/eager/${id}`,
    );
  }

  create(entity: CommandeClient): Observable<CommandeClient> {
    return this.httpClient.post<CommandeClient>(
      `${environment.api()}/commandes_client/create`,
      entity,
    );
  }

  remove(id: string): Observable<string> {
    throw new Error("Method not implemented.");
  }

  update(entity: CommandeClient): Observable<CommandeClient> {
    return this.httpClient.post<CommandeClient>(
      `${environment.api()}/commandes_client`,
      entity,
    );
  }

  loadAll(): Observable<CommandeClient[]> {
    return this.httpClient.get<CommandeClient[]>(
      `${environment.api()}/commandes_client`,
    );
  }

  async updateCommandeLignes(
    lignes: CommandeClientLigne[],
    dateLivraisonPrevue: Date,
    commandeId: string,
  ) {
    return await safeFirstValueFrom(
      this.httpClient.post<CommandeClient>(
        `${environment.api()}/commandes_client/update_lignes/${commandeId}`,
        {
          lignes,
          dateLivraisonPrevue,
        },
      ),
    );
  }

  loadAllFilter(filters: {
    etats: CommandeClientEtat[];
    etatsExpedition?: string[];
    etatsFacturation?: string[];
    etatsPreparation?: string[];
    search: string;
    sort: string;
    /** @deprecated legacy single — préférer entrepotIds[] */
    entrepotId?: string;
    /** Refonte mai 2026 — multi-entrepôts. Repeated query param `entrepotIds=`. */
    entrepotIds?: string[];
    /** Inclure CC sans entrepôt assigné (mode global). Défaut true côté back. */
    includeSansEntrepot?: boolean;
    page?: number;
  }) {
    let params = new HttpParams();

    if (filters.sort) {
      params = params.set("sort", filters.sort);
    }
    if (filters.page != null) {
      params = params.set("page", String(filters.page));
    }
    if (filters.etats && filters.etats.length > 0) {
      // `EN_ATTENTE_CONFIRMATION` côté UI couvre aussi sa variante
      // `EN_ATTENTE_CONFIRMATION_MODIFIED` (même libellé, variante interne).
      const expanded = filters.etats.flatMap((etat) =>
        etat === CommandeClientEtat.EN_ATTENTE_CONFIRMATION
          ? [
              CommandeClientEtat.EN_ATTENTE_CONFIRMATION,
              CommandeClientEtat.EN_ATTENTE_CONFIRMATION_MODIFIED,
            ]
          : [etat],
      );
      expanded.forEach((etat) => {
        params = params.append("etats", etat.toString());
      });
    }
    if (filters.etatsExpedition && filters.etatsExpedition.length > 0) {
      filters.etatsExpedition.forEach((e) => {
        params = params.append("etatsExpedition", e);
      });
    }
    if (filters.etatsFacturation && filters.etatsFacturation.length > 0) {
      filters.etatsFacturation.forEach((e) => {
        params = params.append("etatsFacturation", e);
      });
    }
    if (filters.etatsPreparation && filters.etatsPreparation.length > 0) {
      filters.etatsPreparation.forEach((e) => {
        params = params.append("etatsPreparation", e);
      });
    }
    if (filters.search) {
      params = params.set("search", filters.search);
    }
    if (filters.entrepotId) {
      params = params.set("entrepotId", filters.entrepotId);
    }
    if (filters.entrepotIds && filters.entrepotIds.length > 0) {
      filters.entrepotIds.forEach((id) => {
        params = params.append("entrepotIds", id);
      });
    }
    if (filters.includeSansEntrepot !== undefined) {
      params = params.set(
        "includeSansEntrepot",
        filters.includeSansEntrepot ? "true" : "false",
      );
    }

    return this.httpClient.get<CommandeClient[]>(
      `${environment.api()}/commandes_client/navfilter`,
      {
        params,
      },
    );
  }

  sendMail(payload: CommandeClientMailPayload) {
    return this.httpClient.post<CommandeClient>(
      `${environment.api()}/commandes_client/send_mail`,
      payload,
    );
  }

  async updateDocumentListeId(semId: string, documentListId: string) {
    return await safeFirstValueFrom(
      this.httpClient.post(
        `${environment.api()}/commandes/documentList/${semId}/${documentListId}`,
        null,
      ),
    );
  }

  loadAdresseLivraisonCommande(id: string): Observable<Adresse> {
    return this.httpClient.get<Adresse>(
      `${environment.api()}/commandes_client/adresseLivraison/${id}`,
    );
  }

  getPdfFichePalette(palette: Palette) {
    return this.httpClient.get(
      `${environment.api()}/palette/payload/${palette.id}`,
    );
  }

  cancel(a: CommandeClient) {
    return this.httpClient.post<CommandeClient>(
      `${environment.api()}/commandes_client/cancel`,
      a,
    );
  }

  uncancel(a: CommandeClient) {
    return this.httpClient.post<CommandeClient>(
      `${environment.api()}/commandes_client/uncancel`,
      a,
    );
  }

  confirm(a: CommandeClient) {
    return this.httpClient.post<CommandeClient>(
      `${environment.api()}/commandes_client/confirmer`,
      a,
    );
  }

  terminer(a: CommandeClient) {
    return this.httpClient.post<CommandeClient>(
      `${environment.api()}/commandes_client/terminer`,
      a,
    );
  }

  reopen(a: CommandeClient) {
    return this.httpClient.post<CommandeClient>(
      `${environment.api()}/commandes_client/reopen`,
      a,
    );
  }

  expedierVrac(payload: {
    stocks: VracExpeditionItem[];
    commandeClientId: string;
  }) {
    return this.httpClient.post(
      `${environment.api()}/expedition_commande/expedier-vrac`,
      payload,
    );
  }

  updateRemisePied(id: string, remisePied: number | null, remisePiedType: string | null, remisePiedLabel: string | null) {
    return this.httpClient.post<CommandeClient>(
      `${environment.api()}/commandes_client/${id}/remise_pied`,
      { remisePied, remisePiedType, remisePiedLabel },
    );
  }

  addEntrepot(ccId: string, adresseId: string) {
    return this.httpClient.post<CommandeClient>(
      `${environment.api()}/commandes_client/${ccId}/entrepots/add`,
      { adresseId },
    );
  }

  removeEntrepot(ccId: string, adresseId: string) {
    return this.httpClient.post<CommandeClient>(
      `${environment.api()}/commandes_client/${ccId}/entrepots/remove`,
      { adresseId },
    );
  }
}
